'use client';

import React, { useEffect, useState } from 'react';
import { File } from '@/type/chat';
import useSWRMutation from 'swr/mutation';
import { useRouter } from 'next/navigation';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useChats } from '@/contexts/ChatContext';
import { ChatMode, useChatMode } from '@/contexts/ChatModeContext';
import useSWR from 'swr';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useTranslation } from '@/lib/i18n';
import { Stratege } from '@/components/Workspace/StrategyLab/strategy-config';

interface CreateChatParams {
  message: string;
  teamId?: string;
  source?: string;
  queryType?: 'auto' | 'crypto' | 'predx';
  onSuccess?: ({ chatId, currentQuery }: { chatId: string; currentQuery?: string }) => void;
}
interface UseCreateChatReturn {
  createChat: (params: CreateChatParams) => Promise<void>;
  isCreating: boolean;
  focusMode: string[];
  setFocusMode: React.Dispatch<React.SetStateAction<string[]>>;
  optimizationMode: string;
  setOptimizationMode: React.Dispatch<React.SetStateAction<string>>;
  fileIds: string[];
  setFileIds: React.Dispatch<React.SetStateAction<string[]>>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  strategyType?: Stratege;
  setStrategyType: React.Dispatch<React.SetStateAction<Stratege | undefined>>;
}

export const useCreateChat = (target?: ChatMode): UseCreateChatReturn => {
  const { mode, isWorkspace, workspaceId, agent } = useChatMode();
  const router = useRouter();
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { refreshChats } = useChats();
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const { trigger: updateEstimatedTime } = useSWRMutation('estimatedTime');
  const { trigger: setLoginDialogOpen } = useSWRMutation('loginDialogOpen');
  const { openModal } = useSubscription();
  const [strategyType, setStrategyType] = useState<Stratege>();

  const modeToUse = target || mode;
  // 预加载/c/[chatId]页面资源
  useEffect(() => {
    // 预加载聊天页面组件
    if (modeToUse === 'alfa') {
      router.prefetch('/c/[chatId]');
    }
    if (modeToUse === 'research') {
      router.prefetch('/r/[chatId]');
    }

    if (modeToUse === 'predic') {
      router.prefetch('/predic/r/[chatId]');
    }
  }, [router, modeToUse]);
  const [isCreating, setIsCreating] = React.useState<boolean>(false);
  const [focusMode, setFocusMode] = React.useState<string[]>(['cryptoDynamicRouting']);
  const [optimizationMode, setOptimizationMode] = React.useState<string>('default');
  const [fileIds, setFileIds] = React.useState<string[]>([]);
  const [files, setFiles] = React.useState<File[]>([]);

  const { trigger: createChatFetch } = useSWRMutation(
    modeToUse === 'alfa'
      ? 'api:/chat/create'
      : modeToUse === 'predic'
        ? 'api:/predic/chat/create'
        : 'research:/conversations/start',
  );

  //search limit
  const { searchAllowed, researchAllowed } = useSubscription();
  const searchEnable =
    modeToUse === 'strategy' ? true : modeToUse === 'alfa' ? searchAllowed : researchAllowed;

  const createChat = async ({
    message,
    source,
    onSuccess,
    queryType = 'auto',
  }: CreateChatParams) => {
    try {
      if (!searchEnable && modeToUse !== 'predic') {
        showError(t('Search limit reached') + ', ' + t('Upgrade to keep searching without limits'));
        // if (mode === 'alfa') {
        //   setSearchLimitShow(true)
        // } else {
        //   setReSearchLimitShow(true)
        // }
        openModal();
        return;
      }

      setIsCreating(true);
      const res: any = await createChatFetch({
        method: 'POST',
        body: JSON.stringify({
          content: message,
          focusMode: focusMode,
          files: fileIds,
          source,
          queryType,
          // search/stream 参数
          query: message,
          teamId: isWorkspace && workspaceId ? workspaceId : undefined,
          agent: modeToUse === 'brain' ? agent : modeToUse === 'strategy' ? 'sla' : 'cab',
          alfagptToken: apiAccessToken,
          strategy_type: strategyType,
        }),
      });
      if (onSuccess) {
        setIsCreating(false);
        refreshChats();
        onSuccess?.({ chatId: res.chatId, currentQuery: res.currentQuery });
        return;
      }
      if (modeToUse === 'predic') {
        const query = res.currentQuery ? `?query=${encodeURIComponent(res.currentQuery)}` : '';
        router.push(`/predic/c/${res.chatId}${query}`);
        // 异步刷新聊天列表
        refreshChats();
      } else if (isWorkspace) {
        const url = new URL(location.href);
        url.searchParams.set(
          'chat_id',
          modeToUse === 'alfa' ? res.chatId : res.data.conversationId,
        );
        if (agent) {
          url.searchParams.set('agent', agent);
        }
        if (strategyType) {
          url.searchParams.set('st_type', strategyType);
        }
        router.push(url.href);
        updateEstimatedTime(res.data.estimatedTime * 60000);
        refreshChats();
      } else if (modeToUse !== 'alfa' && res?.data?.conversationId) {
        router.push(`/r/${res.data.conversationId}`);
        updateEstimatedTime(res.data.estimatedTime * 60000);
        refreshChats();
      } else if (res?.chatId) {
        router.push(`/c/${res.chatId}`);
        // 异步刷新聊天列表
        refreshChats();
      } else {
        showError(res?.message || 'Something went wrong');
      }
    } catch (error: any) {
      if (error?.status === 401) {
        setLoginDialogOpen(true);
        return;
      }

      showError(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createChat,
    isCreating,
    focusMode,
    setFocusMode,
    optimizationMode,
    setOptimizationMode,
    fileIds,
    setFileIds,
    files,
    setFiles,
    strategyType,
    setStrategyType,
  };
};
